import { useState, useEffect } from "react";

export default function CMSGallery({ gallerySlug, title = "Galleri" }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    let isMounted = true;

    async function fetchGallery() {
      try {
        setLoading(true);
        const res = await fetch("https://api.dev.auxo.dk/public/content/galleri");
        if (res.ok) {
          const data = await res.json();
          // Filter by gallery_slug or fallback to items array
          const items = Array.isArray(data) ? data : (data.items || []);
          const matched = items.find((item) => item.gallery_slug === gallerySlug);

          if (isMounted) {
            if (matched && matched.images && matched.images.length > 0) {
              setImages(matched.images);
            } else {
              setImages([]);
            }
          }
        }
      } catch (err) {
        console.error("Fejl ved hentning af CMS galleri:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchGallery();

    return () => {
      isMounted = false;
    };
  }, [gallerySlug]);

  const openLightbox = (index) => {
    setSelectedIndex(index);
    setSelectedImage(images[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    const newIdx = (selectedIndex - 1 + images.length) % images.length;
    setSelectedIndex(newIdx);
    setSelectedImage(images[newIdx]);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    const newIdx = (selectedIndex + 1) % images.length;
    setSelectedIndex(newIdx);
    setSelectedImage(images[newIdx]);
  };

  if (loading) {
    return (
      <div className="py-12 text-center text-charcoal-600">
        <i className="fa-solid fa-spinner fa-spin text-2xl text-forest-700 mb-2"></i>
        <p className="text-sm">Henter billeder...</p>
      </div>
    );
  }

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <section className="py-10">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-forest-900 tracking-tight">{title}</h3>
          <p className="text-sm text-charcoal-600 mt-1">Klik på et billede for at åbne det i stor visning.</p>
        </div>
      </div>

      {/* Grid */}
      <div
        className={`grid gap-4 ${
          images.length === 1
            ? "grid-cols-1"
            : images.length === 2
            ? "grid-cols-1 sm:grid-cols-2"
            : images.length === 3
            ? "grid-cols-1 sm:grid-cols-3"
            : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
        }`}
      >
        {images.map((imgUrl, idx) => {
          // Cloudflare resize URL for thumbnail grid
          const thumbUrl = imgUrl.includes("assets.auxodev.dk")
            ? imgUrl.replace(
                "assets.auxodev.dk/",
                "assets.auxodev.dk/cdn-cgi/image/width=600,height=450,fit=cover,format=auto,quality=80/"
              )
            : imgUrl;

          return (
            <button
              key={idx}
              onClick={() => openLightbox(idx)}
              type="button"
              className="group relative block w-full h-64 sm:h-80 lg:h-[450px] overflow-hidden rounded-xl bg-sand-200 border border-sand-300/60 shadow-xs focus:outline-none focus:ring-2 focus:ring-forest-600 cursor-pointer"
            >
              <img
                src={thumbUrl}
                alt={`${title} - Billede ${idx + 1}`}
                className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-forest-950/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                <span className="bg-white/90 text-forest-900 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm flex items-center gap-1.5">
                  <i className="fa-solid fa-expand text-forest-700"></i> Se stor
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-50 flex items-center justify-center bg-forest-950/90 backdrop-blur-xs p-4 cursor-zoom-out"
          aria-modal="true"
          role="dialog"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-5xl w-full max-h-[90vh] flex flex-col items-center justify-center bg-transparent cursor-default"
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute -top-12 right-0 text-white hover:text-sand-200 text-3xl p-2 cursor-pointer transition-colors focus:outline-none"
              aria-label="Luk billede"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>

            {/* Main Image */}
            <div className="relative overflow-hidden rounded-2xl shadow-2xl max-h-[80vh] flex items-center justify-center bg-black/40">
              <img
                src={
                  selectedImage.includes("assets.auxodev.dk")
                    ? selectedImage.replace(
                        "assets.auxodev.dk/",
                        "assets.auxodev.dk/cdn-cgi/image/width=1600,format=auto,quality=90/"
                      )
                    : selectedImage
                }
                alt={`${title} stor visning`}
                className="max-h-[80vh] w-auto max-w-full object-contain"
              />
            </div>

            {/* Navigation buttons */}
            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-forest-900/80 hover:bg-forest-800 text-white p-3 rounded-full shadow-lg cursor-pointer transition-transform hover:scale-105 focus:outline-none"
                  aria-label="Forrige billede"
                >
                  <i className="fa-solid fa-chevron-left text-lg"></i>
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-forest-900/80 hover:bg-forest-800 text-white p-3 rounded-full shadow-lg cursor-pointer transition-transform hover:scale-105 focus:outline-none"
                  aria-label="Næste billede"
                >
                  <i className="fa-solid fa-chevron-right text-lg"></i>
                </button>
              </>
            )}

            {/* Counter */}
            <div className="mt-4 text-center text-xs text-sand-100 font-medium tracking-wide">
              Billede {selectedIndex + 1} af {images.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
